import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '@/auth';
import {
  startSessionRecording,
  stopSessionRecording,
  setTenantContext,
  setInterviewContext,
  clearInterviewContext,
} from './posthog';

// Routes where session recording should be enabled
// Pattern captures interview_id for context tracking
const INTERVIEW_ROUTE_PATTERNS = [
  /^\/interview\/([^/]+)\/assessment$/,
];

/**
 * Extracts interview ID from pathname if on an interview route
 * Returns null if not on an interview route
 */
function extractInterviewId(pathname: string): string | null {
  for (const pattern of INTERVIEW_ROUTE_PATTERNS) {
    const match = pathname.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
}

/**
 * SessionReplayController
 *
 * Controls PostHog session recording and interview context based on current route.
 * - Session recording is only enabled on meeting view routes
 * - Interview ID is set as a super property when on interview routes
 *   (allows filtering all events by interview_id in PostHog dashboard)
 * - For all other routes, session recording is disabled but
 *   event capture and other PostHog features remain active
 */
export function SessionReplayController(): null {
  const location = useLocation();
  const { name: tenantName } = useTenant();

  // Register tenant_name as a super property (sent with all events)
  useEffect(() => {
    if (tenantName) {
      setTenantContext(tenantName);
    }
  }, [tenantName]);

  useEffect(() => {
    const interviewId = extractInterviewId(location.pathname);
    const isInterviewRoute = interviewId !== null;

    if (isInterviewRoute) {
      // Set interview context for filtering events by interview_id
      setInterviewContext(interviewId);
      // Enable session recording on interview routes
      startSessionRecording();
    } else {
      // Clear interview context when leaving interview routes
      clearInterviewContext();
      // Disable session recording on non-interview routes
      stopSessionRecording();
    }

    // Cleanup: clear context and stop recording when component unmounts
    return () => {
      clearInterviewContext();
      stopSessionRecording();
    };
  }, [location.pathname]);

  return null;
}
